import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { PaymentMethod } from '@/types';
import { paymentMethodsService } from '@/services/defaultPaymentMethods/paymentMehodsService';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_REFERENCE } from '@/lib/queryCacheConfig';

export const usePaymentMethods = () => {
  const queryClient = useQueryClient();
  const { handleApiError } = useAuth();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id ?? '';

  const {
    data: paymentMethods = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.paymentMethods(orgId),
    queryFn: async () => {
      if (!currentOrganization) return [];

      try {
        const data = await paymentMethodsService.getPaymentMethods(currentOrganization.id);
        if (error) {
          const isAuthError = await handleApiError(error);
          if (isAuthError) {
            return [];
          }
          throw error;
        }

        return data;
      } catch (error) {
        const isAuthError = await handleApiError(error);
        if (isAuthError) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!currentOrganization,
    staleTime: STALE_TIME_REFERENCE.paymentMethods,
  });

  const createPaymentMethod = useMutation({
    mutationFn: async (method: Omit<PaymentMethod, 'id' | 'createdAt' | 'organizationId'>) => {
      if (!currentOrganization) throw new Error('No organization selected');
      const data = await paymentMethodsService.createPaymentMethod(method, currentOrganization.id);
      if (data) {
        return data;
      }

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods(orgId) });
    },
  });

  const updatePaymentMethod = useMutation({
    mutationFn: async (method: PaymentMethod) => {
      const data = await paymentMethodsService.updatePaymentMethod(method);
      if (data) {
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods(orgId) });
    },
  });

  const deletePaymentMethod = useMutation({
    mutationFn: async (methodId: string) => {
      const data = paymentMethodsService.deletePaymentMethod(methodId);
      if(data){
        return data
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentMethods(orgId) });
    },
  });

  return {
    paymentMethods,
    isLoading,
    error,
    createPaymentMethod: createPaymentMethod.mutate,
    updatePaymentMethod: updatePaymentMethod.mutate,
    deletePaymentMethod: deletePaymentMethod.mutate,
    isCreating: createPaymentMethod.isPending,
    isUpdating: updatePaymentMethod.isPending,
    isDeleting: deletePaymentMethod.isPending,
  };
};
